import { useTrips, useDeleteTrip } from "@/lib/api/hooks";
import { useNavigate } from "react-router-dom";
import type { Trip } from "@/lib/api/schemas";

export default function TripsPage() {
  const { data: trips, isLoading } = useTrips();
  const deleteMutation = useDeleteTrip();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="mx-auto mt-16 w-fit rounded-2xl frost-card px-8 py-5 text-center text-ink-light">
        Loading trips...
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[92rem] px-3 py-10 md:px-4 lg:px-5">
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-ink">My Trips</h1>
          <p className="mt-1 text-sm text-ink-light">Your saved itineraries and active plans.</p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-deep transition hover:bg-accent-light"
        >
          Plan a new trip
        </button>
      </div>

      {!trips?.length ? (
        <div className="rounded-3xl frost-card px-8 py-20 text-center text-ink-light">
          <p className="text-lg text-ink">No trips yet</p>
          <p className="mt-1 text-sm">Start planning your first adventure.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {trips.map((trip: Trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              onOpen={() => navigate(`/plan/${trip.id}`)}
              onDelete={() => deleteMutation.mutate(trip.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TripCard({
  trip,
  onOpen,
  onDelete,
}: {
  trip: Trip;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const dayCount = trip.itinerary?.days?.length || 0;

  return (
    <div className="rounded-2xl frost-card p-5 transition duration-200 hover:-translate-y-0.5">
      <div className="flex items-start justify-between gap-3">
        <button onClick={onOpen} className="flex-1 text-left">
          <h3 className="text-lg font-semibold text-ink">
            {trip.title || trip.destination || "Untitled Trip"}
          </h3>
          {trip.destination && (
            <p className="mt-0.5 text-sm text-ink-light">{trip.destination}</p>
          )}
          <div className="mt-3 flex gap-3 text-xs text-ink-light">
            {dayCount > 0 && <span>{dayCount} days planned</span>}
            <span className="capitalize">{trip.status}</span>
          </div>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="rounded-lg border border-white/15 p-1.5 text-ink-light transition hover:border-red-300 hover:text-red-300"
          title="Delete trip"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
